import { DownloadOutlined } from '@ant-design/icons';
import pluralize from 'pluralize';
import { useEffect, useState } from 'react';

import { Button, Tooltip } from 'components/ui';
import {
  ErrorText,
  SuccessText,
} from 'horizon/components/Assets/CreateAssetsModal/ModalContent/common.style';
import { type ImportResult } from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/types';
import { useCreateSignedGetMutation } from 'types/atlas-graphql';

type ErrorStateProps = {
  importResult: ImportResult | undefined;
  importedFileName: string;
};

export const ErrorState: React.FunctionComponent<ErrorStateProps> = ({
  importResult,
  importedFileName,
}) => {
  const [signedUrl, setSignedUrl] = useState<string | undefined>();
  const [createSignedGet] = useCreateSignedGetMutation();

  const { createdDamageCount, duplicateDamageCount, invalidDamageCount, invalidDamageCsv } =
    importResult ?? {};

  useEffect(() => {
    if (invalidDamageCsv) {
      setSignedUrl(signedUrl);
      fetchSignedUrl();
    } else {
      setSignedUrl(undefined);
    }
  }, [invalidDamageCsv]);

  const fetchSignedUrl = async () => {
    const s3Key = getIngestS3Key();
    try {
      if (s3Key) {
        const { data } = await createSignedGet({
          variables: { input: { ingestS3Key: s3Key } },
        });
        const signedUrl = data?.createSignedGet?.url;
        if (signedUrl) {
          setSignedUrl(signedUrl);
        }
      }
    } catch {
      setSignedUrl(undefined);
    }
  };

  const getIngestS3Key = () => {
    if (!invalidDamageCsv) {
      return undefined;
    }
    try {
      return decodeURIComponent(new URL(invalidDamageCsv).pathname.slice(1));
    } catch {
      return undefined;
    }
  };

  if (importResult?.errorMessage) {
    return (
      <>
        <ErrorText fontWeight="bold" mb={2}>
          {importResult.errorMessage}
        </ErrorText>
      </>
    );
  }

  return (
    <>
      <SuccessText>
        {pluralize('damage', createdDamageCount, true)} successfully created.
      </SuccessText>
      {!!duplicateDamageCount && (
        <>
          <ErrorText fontWeight="bold" mb={2}>
            {pluralize('row', duplicateDamageCount, true)} in {importedFileName} contained duplicate
            damage information.
          </ErrorText>
        </>
      )}
      {!!invalidDamageCount && invalidDamageCsv && (
        <>
          <ErrorText fontWeight="bold" mb={2}>
            {pluralize('row', invalidDamageCount, true)} in {importedFileName} contained invalid or
            incomplete data.
          </ErrorText>
          <ErrorText>
            Please use the button below to download the error file, review and complete faulty or
            missing data and upload again.
          </ErrorText>
          <Tooltip
            title={
              !signedUrl ? `Could not generate error file link for ${importedFileName}.` : null
            }
          >
            <Button
              icon={<DownloadOutlined />}
              href={signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!signedUrl}
            >
              Download Error File
            </Button>
          </Tooltip>
        </>
      )}
    </>
  );
};
